import { ImageScaleView } from '@play-co/timestep-core/ui';
import { PetEntity } from 'src/game/logic/EntityManager';
import { petSpeciesAssets } from 'src/loadingGroups';
import { petAnimationNames } from 'src/replicant/pet/PetAssets';
import { SpeciesChatbotType } from 'src/replicant/chatbot/flex/species';
import { preloadImages } from 'src/lib/utils';
import LangBitmapFontTextView from 'src/lib/ui/components/LangBitmapFontTextView';
import {
  createBackground,
  CreativeAsset,
  getCreativeView,
  renderCreativeView,
} from 'src/creatives/core';

const WIDTH = 720;
const HEIGHT = 477;

// Local asset paths (served by dev server, unlike chatbot CDN paths).
const BG_IMAGE = 'assets/creatives/janken/janken_bg.png';
const SIGNS_IMAGE = 'assets/creatives/janken/janken_invite.png';
const BUTTON_IMAGE = 'assets/creatives/janken/janken_green_button.png';

// Actual image aspect ratios (measured from PNGs).
const SIGNS_ASPECT = 645 / 309; // ~2.09:1
const BUTTON_ASPECT = 575 / 148; // ~3.89:1

/** Wait for bitmap font glyphs to rasterize before renderToDataURL. */
function waitAFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

/**
 * Compose a janken challenge share image matching the LINE flex message layout.
 *
 * Layers: background arena, sender pet, signs capsule, challenge text, play button + CTA.
 * Uses local game assets (not chatbot CDN URLs) so composition works in all environments.
 */
export async function createJankenChallengeCreative(opts: {
  species: SpeciesChatbotType;
  text: string;
  cta: string;
}): Promise<CreativeAsset> {
  const { species, text, cta } = opts;

  await Promise.all([
    preloadImages([BG_IMAGE, SIGNS_IMAGE, BUTTON_IMAGE]),
    petSpeciesAssets[species].load(),
  ]);

  const superview = getCreativeView(WIDTH, HEIGHT);

  // 1. Background
  createBackground({ superview, image: BG_IMAGE });

  // 2. Sender pet — left side, bottom area
  const pet = new PetEntity('jankenShare', species);
  pet.setAnimation(petAnimationNames[species].happy.idle);
  pet.updateOpts({
    superview,
    x: WIDTH * 0.06 + (WIDTH * 0.33) / 2,
    y: HEIGHT - HEIGHT * 0.22 - 30,
    anchorX: 0.5 * pet.style.width,
    anchorY: 0.78 * pet.style.height,
    scale: 0.8,
    zIndex: 2,
  });

  // 3. Signs capsule — right area
  const signsBoxW = WIDTH * 0.8;
  const signsBoxH = HEIGHT * 0.59;
  const signsBoxX = WIDTH - signsBoxW + WIDTH * 0.1;
  const signsBoxY = HEIGHT * 0.2 - 10;
  const signsW = signsBoxW * 0.6;
  const signsH = signsW / SIGNS_ASPECT;

  new ImageScaleView({
    superview,
    image: SIGNS_IMAGE,
    width: signsW,
    height: signsH,
    x: signsBoxX + (signsBoxW - signsW) / 2,
    y: signsBoxY + (signsBoxH - signsH) / 2,
    zIndex: 1,
  });

  // 4. Challenge text — single line (LINE crops top/bottom)
  const challengeText = text.replace(/\n/g, ' ');
  new LangBitmapFontTextView({
    superview,
    font: 'Body',
    color: '#FFFFFF',
    size: 28,
    localeText: () => challengeText,
    align: 'center',
    verticalAlign: 'center',
    x: WIDTH * 0.05,
    y: 25,
    width: WIDTH * 0.9,
    height: HEIGHT * 0.12,
    zIndex: 3,
  });

  // 5. Play button + CTA — moved up 20px to avoid LINE bottom crop
  const buttonW = WIDTH * 0.7 * 0.8;
  const buttonH = buttonW / BUTTON_ASPECT;
  const buttonX = (WIDTH - buttonW) / 2;
  const buttonY = HEIGHT - HEIGHT * 0.03 - buttonH - 30;

  new ImageScaleView({
    superview,
    image: BUTTON_IMAGE,
    width: buttonW,
    height: buttonH,
    x: buttonX,
    y: buttonY,
    zIndex: 4,
  });

  new LangBitmapFontTextView({
    superview,
    font: 'Body',
    color: '#FFFFFF',
    size: 38,
    localeText: () => cta,
    align: 'center',
    verticalAlign: 'center',
    x: buttonX,
    y: buttonY - 7,
    width: buttonW,
    height: buttonH,
    zIndex: 5,
  });

  // Wait a frame for bitmap font glyphs to rasterize before capturing.
  await waitAFrame();

  return renderCreativeView('janken_challenge', superview);
}
